/**
 * Single <SEO/> helper:
 * - document <title>
 * - <meta name="description">
 * - OpenGraph + Twitter card
 * - canonical
 * - <html lang>
 * - optional JSON-LD structured data
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const CLUB = 'Ε.Ο.Σ. Ηλιούπολης · Ilioupolis Alpine Club';
// Short brand suffix for the <title> tag so titles stay under the ~65-char
// limit search engines display before truncating. Full CLUB name is kept for
// og:site_name / og:title.
const TITLE_SUFFIX = 'ΕΟΣ Ηλιούπολης';
// Canonical origin: in production we want canonicals to point at the public
// domain (`https://eosh.gr`) regardless of the host actually serving the page,
// so search engines de-dupe preview, CDN edge and apex hostnames into one URL.
// Override with `REACT_APP_CANONICAL_ORIGIN` if the domain ever changes.
const CANONICAL_ORIGIN = (process.env.REACT_APP_CANONICAL_ORIGIN || 'https://eosh.gr').replace(/\/$/, '');
const DEFAULT_IMG = `${CANONICAL_ORIGIN}/logo512.png`;

const SEO = ({
  title,
  description,
  image,
  imageWidth,
  imageHeight,
  type = 'website',
  jsonLd,
  noIndex = false,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'gr' ? 'el' : 'en';
  const { pathname } = useLocation();
  const url = `${CANONICAL_ORIGIN}${pathname}`;
  let fullTitle = title ? `${title} · ${TITLE_SUFFIX}` : CLUB;
  if (fullTitle.length > 65) fullTitle = `${fullTitle.slice(0, 62).trimEnd()}…`;
  const desc = description || (lang === 'el'
    ? 'Ε.Ο.Σ. Ηλιούπολης — αναβάσεις, σχολές, εκδηλώσεις, εθελοντισμός. Ο ιστορικός ορειβατικός σύλλογος της Αθήνας.'
    : 'Ilioupolis Alpine Club — ascents, courses, events and volunteering. The historic mountaineering club of Athens.');
  // Iter240 — Facebook / Twitter / LinkedIn require og:image to be an
  // ABSOLUTE URL. Trip / class / event pages pass hero images like
  // "/api/cms/media/cms_xxx.jpg" (root-relative), which social crawlers
  // silently drop, falling back to the default club-logo og-image. Prepend
  // the canonical origin so shared previews finally show the actual hero.
  const rawImg = image || DEFAULT_IMG;
  const img = /^https?:\/\//i.test(rawImg) ? rawImg : `${CANONICAL_ORIGIN}${rawImg.startsWith('/') ? '' : '/'}${rawImg}`;
  // Iter390 — Facebook/Instagram need explicit dimensions + a secure_url,
  // otherwise the first crawl renders a tiny/blank preview and that broken
  // result gets cached. Share cards are always 1200×630; the fallback club
  // logo is 512×512.
  const ogW = imageWidth || (image ? 1200 : 512);
  const ogH = imageHeight || (image ? 630 : 512);
  const secureImg = img.replace(/^http:\/\//i, 'https://');

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={CLUB} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:secure_url" content={secureImg} />
      <meta property="og:image:type" content={/\.png$/i.test(img) ? 'image/png' : 'image/jpeg'} />
      <meta property="og:image:width" content={String(ogW)} />
      <meta property="og:image:height" content={String(ogH)} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content={lang === 'el' ? 'el_GR' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

/* ---------- JSON-LD builders ---------------------------------------- */

export const breadcrumbLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const tripLd = (trip, url, img) => {
  // Iter240 — same absolute-URL requirement as og:image; JSON-LD is picked
  // up by Google + Bing + LinkedIn crawlers and they all silently discard
  // relative URLs. Normalise once so callers can keep passing "/api/..." strings.
  const absImg = img && /^https?:\/\//i.test(img)
    ? img
    : (img ? `https://eosh.gr${img.startsWith('/') ? '' : '/'}${img}` : undefined);
  return {
  '@context': 'https://schema.org',
  '@type': trip.kind === 'class' ? 'Course' : (trip.kind === 'event' ? 'Event' : 'SportsEvent'),
  name: trip.title || trip.title_gr,
  description: (trip.description || '').replace(/<[^>]+>/g, '').slice(0, 500),
  startDate: trip.start_date,
  endDate: trip.end_date || trip.start_date,
  image: absImg,
  url,
  location: {
    '@type': 'Place',
    name: trip.location || trip.category || 'Greece',
    address: { '@type': 'PostalAddress', addressCountry: 'GR' },
  },
  organizer: {
    '@type': 'Organization',
    name: CLUB,
    url: typeof window !== 'undefined' ? window.location.origin : 'https://eosh.gr',
  },
  offers: trip.member_price_num != null ? {
    '@type': 'Offer',
    price: trip.guest_price_num || trip.member_price_num,
    priceCurrency: 'EUR',
    url,
    availability: trip.cancelled
      ? 'https://schema.org/Discontinued'
      : (trip.seats_remaining === 0
          ? 'https://schema.org/SoldOut'
          : 'https://schema.org/InStock'),
  } : undefined,
  };
};
